import React, { Fragment, useEffect } from 'react';
import Article from '@gik/blog/models/Article';
import ArticleLinkCardList from '@gik/blog/components/ArticleLinkCardList';
import { ArticleLinkCardStyle } from '@gik/blog/components/ArticleLinkCard';
import { LoadingSpinner } from '@gik/ui';
import RedisClient, { WordpressSortOption } from '../../../utils/RedisClient';
import WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import WordpressSearchResult from '@gik/core/models/wordpress/WordpressSearchResult';
import Dropdown from '@gik/blog/components/Dropdown';
import WordpressArticleType from '@gik/core/models/wordpress/WordpressArticleType';
import WordpressSituation from '@gik/core/models/wordpress/WordpressSituation';
import WordpressTag from '@gik/core/models/wordpress/WordpressTag';
import Category from '@gik/blog/models/Category';
import Situation from '@gik/blog/models/Situation';
import ArticleType from '@gik/blog/models/ArticleType';
import getPreviewMode from '@gik/core/utils/PreviewMode';

interface ArticleDropdownOption {
  name: string;
}

const pageSize = 8;
const allFilterOption: ArticleDropdownOption = { name: 'All' };
const sortOptions: ArticleDropdownOption[] = Object.values(WordpressSortOption).map(value => ({ name: value }));

interface ArticleSearchPageResultsProps {
  searchQuery: string;
  tags: WordpressTag[];
  situations: WordpressSituation[];
  articleTypes: WordpressArticleType[];
  userId: string;
}

export default function ArticleSearchPageResults(props: ArticleSearchPageResultsProps) {
  return <_ArticleSearchPageResults {...props} />;
}

// TODO: convert below to a functional component
interface ArticleSearchPageResultsState {
  articles: Article[];
  searchResults: WordpressSearchResult[];
  searchResultsIndex: number;
  loading: boolean;
  paging: boolean;
  tags: Category[];
  situations: Situation[];
  articleTypes: ArticleType[];
  selectedTag: Category;
  selectedSituation: Situation;
  selectedArticleType: ArticleType;
  sortBy: ArticleDropdownOption;
  refineVisible: boolean;
  refineAnimationFinished: boolean;
}

class _ArticleSearchPageResults extends React.Component<ArticleSearchPageResultsProps, ArticleSearchPageResultsState> {
  constructor(props: ArticleSearchPageResultsProps) {
    super(props);
    this.state = {
      articles: [],
      searchResultsIndex: 0,
      searchResults: [],
      loading: true,
      paging: false,
      tags: [
        allFilterOption as Category,
        ...props.tags.map(WordpressConverter.convertTag).filter(tag => tag.postCount > 0),
      ],
      situations: [allFilterOption as Situation, ...props.situations.map(WordpressConverter.convertSituation)],
      articleTypes: [allFilterOption as ArticleType, ...props.articleTypes.map(WordpressConverter.convertArticleType)],
      selectedTag: allFilterOption as Category,
      selectedSituation: allFilterOption as Situation,
      selectedArticleType: allFilterOption as ArticleType,
      sortBy: sortOptions[0],
      refineVisible: false,
      refineAnimationFinished: false,
    };
    this._initialFetch();
  }

  render() {
    return (
      <div className="blog search article-search-page article-search-results">
        {!this.state.loading ? (
          <Fragment>
            <div className="results-count-wrapper">
              <p className="results-count">
                {this.state.searchResults.length} articles found matching: "{this.props.searchQuery}"
              </p>
            </div>
            <div className="bs-padding-wrapper">
              <div className="bs-padding">
                <div className="sort-by-wrapper">
                  <div
                    className={`refine-toggle ${this.state.refineVisible ? 'selected' : ''}`}
                    onClick={() => {
                      this.setState(prevState => ({
                        ...prevState,
                        refineVisible: !prevState.refineVisible,
                        refineAnimationFinished: false,
                      }));
                      setTimeout(
                        () => this.setState(prevState => ({ ...prevState, refineAnimationFinished: true })),
                        1000
                      );
                    }}
                  >
                    <span className={`gik-icon-v2 icon-v2-refine`}></span>
                    <p className="refine-toggle-text">Refine</p>
                  </div>
                  {this._renderDropdown(
                    'Sort By',
                    this.state.sortBy,
                    sortOptions,
                    selectedValue =>
                      selectedValue != this.state.sortBy &&
                      this.setState({ ...this.state, sortBy: selectedValue }, this._initialFetch)
                  )}
                </div>
              </div>
            </div>
            <div
              className={`refine ${this.state.refineVisible ? 'open' : ''} ${
                this.state.refineVisible && this.state.refineAnimationFinished ? 'opened' : ''
              }`}
            >
              <div className="bs-padding-wrapper">
                <div className="bs-padding">
                  <div className="refine-inner">
                    {this._renderDropdown(
                      'Situation',
                      this.state.selectedSituation,
                      this.state.situations,
                      selectedValue =>
                        selectedValue != this.state.selectedSituation &&
                        this.setState({ ...this.state, selectedSituation: selectedValue }, this._initialFetch)
                    )}
                    {this._renderDropdown(
                      'Tag',
                      this.state.selectedTag,
                      this.state.tags,
                      selectedValue =>
                        selectedValue != this.state.selectedTag &&
                        this.setState({ ...this.state, selectedTag: selectedValue }, this._initialFetch)
                    )}
                    {this._renderDropdown(
                      'Article Type',
                      this.state.selectedArticleType,
                      this.state.articleTypes,
                      selectedValue =>
                        selectedValue != this.state.selectedArticleType &&
                        this.setState({ ...this.state, selectedArticleType: selectedValue }, this._initialFetch)
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bs-padding-wrapper">
              <div className="bs-padding">
                <div className="multiple-rows-content">
                  {this.state.articles.length ? (
                    <ArticleLinkCardList
                      articleLinks={this.state.articles}
                      style={ArticleLinkCardStyle.small}
                      showAll
                      showMoreButton={this.state.searchResultsIndex < this.state.searchResults.length}
                      loading={this.state.paging}
                      pageSize={pageSize}
                      onShowMore={this._loadMore}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </Fragment>
        ) : (
          <LoadingSpinner center />
        )}
      </div>
    );
  }

  private _renderDropdown(
    dropdownTitle: string,
    selectedOption: ArticleDropdownOption,
    options: ArticleDropdownOption[],
    onSelected: (selectedValue: any) => void
  ) {
    return (
      <div className="dropdown-wrapper">
        <p className="dropdown-wrapper-title">{dropdownTitle}</p>
        <Dropdown
          closeOnClickInside
          hoverDisabled
          heading={selectedOption.name}
          menu={
            <Fragment>
              {options.map(option => (
                <div
                  key={option.name}
                  className={`menu-item ${option.name === selectedOption.name ? 'selected' : ''}`}
                  onClick={() => onSelected(option)}
                >
                  <p key={option.name}>{option.name}</p>
                </div>
              ))}
            </Fragment>
          }
        />
      </div>
    );
  }

  private _initialFetch = async () => {
    this.setState({
      ...this.state,
      loading: true,
      articles: [],
      searchResultsIndex: 0,
      searchResults: [],
    });
    const postsSearchResponse = await RedisClient.searchForPosts(
      this.props.searchQuery,
      null,
      this.state.selectedSituation.slug,
      this.state.selectedTag.slug,
      this.state.selectedArticleType.slug,
      Object.values(WordpressSortOption).find(v => v === this.state.sortBy.name)
    );
    const searchResults = Object.values(postsSearchResponse.data) as WordpressSearchResult[];

    console.log('Search results: ', searchResults);

    this.setState(
      {
        searchResults,
      },
      this._loadMore
    );
  };

  private _loadMore = async () => {
    if (this.state.searchResultsIndex > this.state.searchResults.length) {
      return;
    }
    this.setState(prevState => ({ ...prevState, paging: true }));

    const nextPagePostIds = this.state.searchResults
      .slice(this.state.searchResultsIndex, this.state.searchResultsIndex + pageSize)
      .map(result => result.id.toString());
    const fullPostsResponse = await RedisClient.getPostsByIds(nextPagePostIds);
    const fullPosts = Object.values(fullPostsResponse.data) as WordpressPost[];

    this.setState(prevState => ({
      ...prevState,
      articles: [
        ...this.state.articles,
        ...fullPosts
          .map(post => WordpressConverter.convertPost(post))
          .filter(article => !article.isPreviewOnly || article.isPreviewOnly === getPreviewMode()),
      ],
      searchResultsIndex: this.state.searchResultsIndex + pageSize,
      loading: false,
      paging: false,
    }));
  };
}
